/**
 * Device & User Deactivation/Reactivation Wizards
 * 
 * Flow: Configure all options first → Review summary → Confirm & Execute (live log with smart waits)
 * 
 * Device Deactivation: 6 steps (confirm → GPRS disable → remove users → deactivate SIM → billing → review & execute)
 * Device Reactivation: 6 steps (confirm → reactivate SIM → GPRS enable → allocate users → billing → review & execute)
 * User Deactivation: 5 steps (confirm → deactivate devices → remove allocations → deactivate subs → review & execute)
 */
import React, { useState, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { AlertTriangle, CheckCircle, XCircle, ChevronRight, ChevronLeft, Loader2, Radio, Power, Send, Circle, SkipForward, Clock } from 'lucide-react';
import { toast } from 'sonner';

// Display-name preference: show company name when the user chose it (personal name stays in audit logs).
const userDisplayName = (u) => (u?.display_name_preference === 'company' && (u?.company || '').trim())
  ? u.company.trim()
  : (u?.full_name || u?.email || '');

// ═══════════════════════════════════════════════════════
// SHARED: Execution Log Entry Component
// ═══════════════════════════════════════════════════════

const ExecutionLogEntry = ({ entry, onAction }) => {
  const icon = {
    pending: <Circle className="w-4 h-4 text-muted-foreground shrink-0" />,
    running: <Loader2 className="w-4 h-4 text-blue-500 animate-spin shrink-0" />,
    success: <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />,
    failed: <XCircle className="w-4 h-4 text-red-500 shrink-0" />,
    waiting: <Clock className="w-4 h-4 text-amber-500 animate-pulse shrink-0" />,
    timeout: <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />,
    skipped: <SkipForward className="w-4 h-4 text-muted-foreground shrink-0" />,
  }[entry.status] || <Circle className="w-4 h-4 text-muted-foreground shrink-0" />;

  const bg = {
    success: 'bg-green-50 dark:bg-green-950',
    failed: 'bg-red-50 dark:bg-red-950',
    waiting: 'bg-amber-50 dark:bg-amber-950',
    timeout: 'bg-amber-50 dark:bg-amber-950',
    running: 'bg-blue-50 dark:bg-blue-950',
  }[entry.status] || 'bg-muted/30';

  return (
    <div data-testid={`exec-log-${entry.key}`} className={`flex items-start gap-2 p-2 rounded-lg text-sm ${bg}`}>
      {icon}
      <div className="flex-1 min-w-0">
        <span className="font-medium">{entry.label}</span>
        {entry.message && <p className="text-xs text-muted-foreground mt-0.5 break-all">{entry.message}</p>}
        {entry.status === 'timeout' && entry.actions?.length > 0 && (
          <div className="flex gap-2 mt-1.5">
            {entry.actions.map(a => (
              <Button key={a.key} size="sm" variant={a.variant || 'outline'} className="h-6 text-xs" onClick={() => onAction?.(a.key)}>
                {a.label}
              </Button>
            ))}
          </div>
        )}
        {entry.status === 'waiting' && entry.showSkip && (
          <Button size="sm" variant="outline" className="h-6 text-xs mt-1.5" onClick={() => onAction?.('skip_wait')}>
            Skip Wait
          </Button>
        )}
      </div>
    </div>
  );
};

// Helper: sleep
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

// Helper: split a long Teltonika setparam SMS into <=160-char messages.
// Each part carries its own "setparam" prefix (and PIN) so every SMS is a valid standalone command.
const SMS_MAX_LEN = 160;
const buildTeltonikaSmsParts = (pin, paramString) => {
  const prefix = `${pin || ' '} `;
  const full = `${prefix}${paramString}`;
  if (full.length <= SMS_MAX_LEN) return [full];
  const params = paramString.replace(/^setparam\s+/, '').split(';');
  const parts = [];
  let current = [];
  for (const p of params) {
    const candidate = `${prefix}setparam ${[...current, p].join(';')}`;
    if (candidate.length > SMS_MAX_LEN && current.length > 0) {
      parts.push(`${prefix}setparam ${current.join(';')}`);
      current = [p];
    } else {
      current.push(p);
    }
  }
  if (current.length > 0) parts.push(`${prefix}setparam ${current.join(';')}`);
  return parts;
};

// Helper: log wizard execution to audit trail
const logWizardAudit = async (api, wizardType, targetType, targetId, targetName, executionLog) => {
  try {
    await api.post('/admin/wizard-audit-log', {
      wizard_type: wizardType,
      target_type: targetType,
      target_id: targetId,
      target_name: targetName,
      steps: executionLog.map(e => ({ key: e.key, label: e.label, status: e.status, message: e.message || '' })),
    });
  } catch {}
};


// ═══════════════════════════════════════════════════════
// DEVICE DEACTIVATION WIZARD
// ═══════════════════════════════════════════════════════

export const DeviceDeactivationWizard = ({ open, onClose, device, api, onComplete }) => {
  const [step, setStep] = useState(1);

  // Configuration choices
  const [gprsMethod, setGprsMethod] = useState('skip');
  const [removeAllocations, setRemoveAllocations] = useState('skip');
  const [keepUserIds, setKeepUserIds] = useState([]);
  const [allocatedUserDetails, setAllocatedUserDetails] = useState([]);
  const [deactivateSim, setDeactivateSim] = useState('skip');
  const [removeBilling, setRemoveBilling] = useState('skip');

  // Execution state
  const [isExecutionPhase, setIsExecutionPhase] = useState(false);
  const [executionLog, setExecutionLog] = useState([]);
  const [executionDone, setExecutionDone] = useState(false);
  const [showCloseWarning, setShowCloseWarning] = useState(false);
  const skipWaitRef = useRef(false);
  const abortRef = useRef(false);
  const waitResolverRef = useRef(null);

  const totalSteps = 6;
  const protocol = device?.protocol?.toLowerCase() || '';
  const pin = device?.device_password || '';
  const isMeitrack = protocol.includes('meitrack');
  const isTeltonika = protocol.includes('teltonika');
  const hasSim = !!(device?.sim_iccid);
  // 1NCE / Truphone (1GLOBAL) SIMs must never be deactivated via the provider API
  const isProtectedSimProvider = /truphone|1nce|1global/i.test(device?.sim_provider || '');

  const TELTONIKA_DISABLE_PARAMS = 'setparam 10000:0;10100:0;10200:0;10005:0;10105:0;10205:0;10050:0;10150:0;10250:0;10052:0;10152:0;10252:0;10055:0;10155:0;10255:0;2000:0';

  const getGprsCommand = () => {
    if (isMeitrack) return 'A21,0';
    if (isTeltonika) return TELTONIKA_DISABLE_PARAMS;
    return null;
  };

  const getSmsCommandParts = () => {
    if (isMeitrack) return [`${pin || '0000'},A21,0`];
    if (isTeltonika) return buildTeltonikaSmsParts(pin, TELTONIKA_DISABLE_PARAMS);
    return [];
  };
  const smsCommandParts = getSmsCommandParts();

  const updateLog = useCallback((key, updates) => {
    setExecutionLog(prev => prev.map(e => e.key === key ? { ...e, ...updates } : e));
  }, []);

  // Poll GPRS command status
  // For GPRS-disable commands (setparam ...;2000:0), the device can't respond
  // because GPRS is being disabled. So 'sent' = success (command left the server).
  const pollCommandStatus = async (commandId) => {
    const maxAttempts = 24; // 120s total
    for (let i = 0; i < maxAttempts; i++) {
      if (skipWaitRef.current || abortRef.current) return 'skipped';
      try {
        const res = await api.get(`/parsing/commands/by-id/${commandId}`);
        const status = res.data?.status;
        // 'sent' means command was delivered to device via TCP — success for GPRS-disable
        // 'acknowledged' means device explicitly responded — also success
        // 'timeout' from CommandSender means sent but no reply (expected for GPRS-disable)
        if (status === 'sent' || status === 'acknowledged' || status === 'timeout') return 'acknowledged';
        if (status === 'failed') return 'failed';
      } catch {}
      updateLog('wait', {
        status: 'waiting', showSkip: true,
        message: `Polling command status... Attempt ${i + 1}/${maxAttempts} — ${(i + 1) * 5}s elapsed`,
      });
      await sleep(5000);
    }
    return 'timeout';
  };

  // Poll SMS delivery by checking the provider's SMS history for:
  // ONLY an incoming reply from the device after our send time.
  // Outgoing "Delivered" just means the carrier accepted it, NOT device receipt.
  const pollSmsDelivery = async (simCardIdForPoll, sentCommandText, sentAtTime) => {
    const maxAttempts = 24;
    const sentTime = new Date(sentAtTime);
    for (let i = 0; i < maxAttempts; i++) {
      if (skipWaitRef.current || abortRef.current) return 'skipped';
      try {
        const res = await api.get(`/admin/sim-cards/${simCardIdForPoll}/sms/history`);
        const messages = Array.isArray(res.data) ? res.data : (res.data?.messages || []);
        for (const msg of messages) {
          const msgTime = msg.created_at ? new Date(msg.created_at) : null;
          // Only incoming reply from device confirms receipt
          if (msg.direction === 'incoming' && msgTime && msgTime > sentTime) {
            return 'delivered';
          }
        }
      } catch {}
      updateLog('wait', {
        status: 'waiting', showSkip: true,
        message: `Waiting for device reply... Attempt ${i + 1}/${maxAttempts} — ${(i + 1) * 5}s elapsed`,
      });
      await sleep(5000);
    }
    return 'timeout';
  };

  const handleExecute = async () => {
    setIsExecutionPhase(true);
    skipWaitRef.current = false;
    abortRef.current = false;

    const needsWait = gprsMethod !== 'skip' && deactivateSim === 'deactivate' && hasSim;

    // Build execution plan
    const plan = [{ key: 'deactivate', label: 'Deactivate device', status: 'pending' }];
    if (gprsMethod !== 'skip') {
      plan.push({ key: 'gprs', label: `Send GPRS disable via ${gprsMethod === 'sms' ? 'SMS' : 'GPRS'}`, status: 'pending' });
    }
    if (needsWait) {
      plan.push({ key: 'wait', label: 'Waiting for device acknowledgement', status: 'pending' });
    }
    if (removeAllocations === 'remove_all') {
      plan.push({ key: 'allocations', label: 'Remove all user allocations', status: 'pending' });
    } else if (removeAllocations === 'select') {
      const removeCount = (device.allocated_tenant_ids?.length || 0) - keepUserIds.length;
      if (removeCount > 0) {
        plan.push({ key: 'allocations', label: `Remove ${removeCount} user(s), keep ${keepUserIds.length}`, status: 'pending' });
      }
    }
    if (deactivateSim === 'deactivate' && hasSim) {
      plan.push({ key: 'sim', label: 'Deactivate SIM card', status: 'pending' });
    }
    if (removeBilling === 'remove') {
      plan.push({ key: 'billing', label: 'Remove from billing', status: 'pending' });
    }
    setExecutionLog(plan);
    await sleep(100);

    // 1. Deactivate device
    updateLog('deactivate', { status: 'running' });
    try {
      await api.put(`/admin/devices/${device.id}`, { is_active: false });
      updateLog('deactivate', { status: 'success', message: 'Device set to inactive' });
    } catch (e) {
      updateLog('deactivate', { status: 'failed', message: e.response?.data?.detail || 'Failed to deactivate' });
    }
    if (abortRef.current) return;

    // 2. Send GPRS/SMS command
    let commandId = null;
    let simCardId = null;
    let sentCommandText = null;
    let sentAtTime = new Date().toISOString();

    if (gprsMethod === 'gprs') {
      updateLog('gprs', { status: 'running' });
      try {
        const cmd = getGprsCommand();
        const res = await api.post('/parsing/commands/send', { device_imei: device.imei, command: cmd, command_type: 'custom' });
        commandId = res.data?.command?.id;
        updateLog('gprs', { status: 'success', message: `GPRS command queued: ${cmd}` });
      } catch (e) {
        updateLog('gprs', { status: 'failed', message: e.response?.data?.detail || 'Failed to queue GPRS command' });
      }
    } else if (gprsMethod === 'sms') {
      updateLog('gprs', { status: 'running' });
      try {
        const smsParts = getSmsCommandParts();
        sentCommandText = smsParts.join(' | ');
        sentAtTime = new Date().toISOString();
        const simRes = await api.get(`/admin/sim-cards/search-by-iccid/${encodeURIComponent(device.sim_iccid)}`);
        simCardId = simRes.data?.id;
        if (simCardId) {
          for (let pi = 0; pi < smsParts.length; pi++) {
            updateLog('gprs', { status: 'running', message: `Sending SMS ${pi + 1}/${smsParts.length}: ${smsParts[pi]}` });
            await api.post(`/admin/sim-cards/${simCardId}/sms/send`, { message: smsParts[pi] });
            if (pi < smsParts.length - 1) await sleep(2000);
          }
          updateLog('gprs', { status: 'success', message: smsParts.length > 1 ? `${smsParts.length} SMS messages sent (command split at 160 chars, each with its own setparam)` : `SMS sent: ${smsParts[0]}` });
        } else {
          updateLog('gprs', { status: 'failed', message: 'SIM card not found for SMS' });
        }
      } catch (e) {
        updateLog('gprs', { status: 'failed', message: e.response?.data?.detail || 'Failed to send SMS' });
      }
    }
    if (abortRef.current) return;

    // 3. Wait for acknowledgement (if command sent AND SIM deactivation selected)
    if (needsWait && (commandId || (gprsMethod === 'sms' && simCardId))) {
      updateLog('wait', { status: 'waiting', showSkip: true, message: 'Starting to poll...' });

      let waitResult;
      if (gprsMethod === 'gprs' && commandId) {
        waitResult = await pollCommandStatus(commandId);
      } else if (gprsMethod === 'sms' && simCardId) {
        waitResult = await pollSmsDelivery(simCardId, sentCommandText, sentAtTime);
      }

      if (waitResult === 'acknowledged' || waitResult === 'delivered') {
        updateLog('wait', { status: 'success', showSkip: false, message: gprsMethod === 'gprs' ? 'Command sent to device via TCP' : 'SMS delivered — device confirmed', actions: [] });
      } else if (waitResult === 'skipped') {
        updateLog('wait', { status: 'skipped', showSkip: false, message: 'Wait skipped by admin', actions: [] });
      } else if (waitResult === 'timeout') {
        // Show retry options
        const actions = [{ key: 'proceed', label: 'Proceed Anyway', variant: 'outline' }];
        if (gprsMethod === 'gprs' && hasSim) {
          actions.unshift({ key: 'retry_sms', label: 'Retry via SMS', variant: 'default' });
        }
        updateLog('wait', {
          status: 'timeout', showSkip: false,
          message: 'No response received after 120s',
          actions,
        });

        // Pause execution — wait for user decision
        const decision = await new Promise(resolve => { waitResolverRef.current = resolve; });

        if (decision === 'retry_sms') {
          // Send SMS fallback and poll again
          updateLog('wait', { status: 'waiting', showSkip: true, message: 'Retrying via SMS...', actions: [] });
          try {
            const smsParts = getSmsCommandParts();
            sentCommandText = smsParts.join(' | ');
            sentAtTime = new Date().toISOString();
            if (!simCardId) {
              const simRes = await api.get(`/admin/sim-cards/search-by-iccid/${encodeURIComponent(device.sim_iccid)}`);
              simCardId = simRes.data?.id;
            }
            if (simCardId) {
              for (let pi = 0; pi < smsParts.length; pi++) {
                await api.post(`/admin/sim-cards/${simCardId}/sms/send`, { message: smsParts[pi] });
                if (pi < smsParts.length - 1) await sleep(2000);
              }
              const retryResult = await pollSmsDelivery(simCardId, sentCommandText, sentAtTime);
              if (retryResult === 'delivered') {
                updateLog('wait', { status: 'success', showSkip: false, message: 'SMS delivered — device confirmed' });
              } else if (retryResult === 'skipped') {
                updateLog('wait', { status: 'skipped', showSkip: false, message: 'Wait skipped by admin' });
              } else {
                updateLog('wait', { status: 'timeout', showSkip: false, message: 'SMS delivery not confirmed — proceeding anyway', actions: [] });
              }
            }
          } catch (e) {
            updateLog('wait', { status: 'failed', showSkip: false, message: `SMS retry failed: ${e.response?.data?.detail || 'Error'}`, actions: [] });
          }
        } else {
          updateLog('wait', { status: 'skipped', showSkip: false, message: 'Proceeding without device confirmation', actions: [] });
        }
      } else if (waitResult === 'failed') {
        updateLog('wait', { status: 'failed', showSkip: false, message: 'Command failed on device' });
      }
    } else if (needsWait) {
      // Command wasn't successfully sent, skip wait
      updateLog('wait', { status: 'skipped', showSkip: false, message: 'Skipped — command was not sent' });
    }
    if (abortRef.current) return;

    // 4. Remove allocations
    if (removeAllocations === 'remove_all') {
      updateLog('allocations', { status: 'running' });
      try {
        await api.put(`/admin/devices/${device.id}/allocation`, { allocated_user_ids: [], confirm_remove_all: true });
        updateLog('allocations', { status: 'success', message: 'All user allocations removed' });
      } catch (e) {
        updateLog('allocations', { status: 'failed', message: e.response?.data?.detail || 'Failed' });
      }
    } else if (removeAllocations === 'select') {
      const removeCount = (device.allocated_tenant_ids?.length || 0) - keepUserIds.length;
      if (removeCount > 0) {
        updateLog('allocations', { status: 'running' });
        try {
          await api.put(`/admin/devices/${device.id}/allocation`, { allocated_user_ids: keepUserIds });
          updateLog('allocations', { status: 'success', message: `Kept ${keepUserIds.length} user(s), removed ${removeCount}` });
        } catch (e) {
          updateLog('allocations', { status: 'failed', message: e.response?.data?.detail || 'Failed' });
        }
      }
    }
    if (abortRef.current) return;

    // 5. Deactivate SIM
    if (deactivateSim === 'deactivate' && hasSim) {
      updateLog('sim', { status: 'running' });
      try {
        if (!simCardId) {
          const simRes = await api.get(`/admin/sim-cards/search-by-iccid/${encodeURIComponent(device.sim_iccid)}`);
          simCardId = simRes.data?.id;
        }
        if (simCardId) {
          await api.put(`/admin/sim-cards/${simCardId}/status`, null, { params: { new_status: 'DEACTIVATED' } });
          updateLog('sim', { status: 'success', message: 'SIM card deactivated via provider API' });
        } else {
          updateLog('sim', { status: 'failed', message: 'SIM card not found' });
        }
      } catch (e) {
        updateLog('sim', { status: 'failed', message: e.response?.data?.detail || 'Failed to deactivate SIM' });
      }
    }
    if (abortRef.current) return;

    // 6. Billing
    if (removeBilling === 'remove') {
      updateLog('billing', { status: 'running' });
      try {
        await api.put(`/admin/devices/${device.id}`, { billing_excluded: true });
        updateLog('billing', { status: 'success', message: 'Removed from billing' });
      } catch (e) {
        updateLog('billing', { status: 'failed', message: e.response?.data?.detail || 'Failed' });
      }
    }

    setExecutionDone(true);
    logWizardAudit(api, 'device_deactivation', 'device', device.id, device.name, executionLog);
  };

  const handleClose = () => {
    abortRef.current = true;
    skipWaitRef.current = true;
    waitResolverRef.current?.('proceed');
    setStep(1);
    setGprsMethod('skip');
    setRemoveAllocations('skip');
    setKeepUserIds([]);
    setAllocatedUserDetails([]);
    setDeactivateSim('skip');
    setRemoveBilling('skip');
    setExecutionLog([]);
    setIsExecutionPhase(false);
    setExecutionDone(false);
    setShowCloseWarning(false);
    onClose();
    if (executionDone) onComplete?.();
  };

  const handleDialogClose = () => {
    if (isExecutionPhase && !executionDone) {
      setShowCloseWarning(true);
    } else {
      handleClose();
    }
  };

  const handleWaitAction = (action) => {
    if (action === 'skip_wait') {
      skipWaitRef.current = true;
    } else {
      waitResolverRef.current?.(action);
    }
  };

  if (!device) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleDialogClose(); }}>
      <DialogContent className="max-w-xl h-[620px] max-h-[90vh] flex flex-col" data-testid="device-deactivation-wizard" onPointerDownOutside={(e) => { if (isExecutionPhase && !executionDone) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Power className="w-5 h-5 text-red-500" />
            Deactivate Device
          </DialogTitle>
          <DialogDescription>
            {device.name} ({device.imei || device.mac_address})
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-1 mb-2">
          {Array.from({ length: totalSteps }, (_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full ${i + 1 <= step ? 'bg-red-500' : 'bg-muted'}`} />
          ))}
          <span className="text-xs text-muted-foreground ml-2">Step {step}/{totalSteps}</span>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-3">

        {/* Step 1: Confirm */}
        {step === 1 && (
          <div className="space-y-3" data-testid="wizard-step-1">
            <div className="p-4 bg-red-50 dark:bg-red-950 rounded-lg border border-red-200">
              <p className="text-sm font-medium">Are you sure you want to deactivate this device?</p>
              <p className="text-xs text-muted-foreground mt-1">The device will stop connecting to the server. You can reactivate it later.</p>
            </div>
          </div>
        )}

        {/* Step 2: GPRS disable */}
        {step === 2 && (
          <div className="space-y-3" data-testid="wizard-step-2">
            <p className="text-sm font-medium">Send GPRS disable command?</p>
            <p className="text-xs text-muted-foreground">This prevents the device from locking up when trying to upload data after SIM deactivation.</p>
            {(isTeltonika || isMeitrack) ? (
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setGprsMethod('gprs')}>
                  <input type="radio" name="gprs" className="shrink-0" checked={gprsMethod === 'gprs'} onChange={() => setGprsMethod('gprs')} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">Send via GPRS</p>
                    <p className="text-xs text-muted-foreground font-mono break-all">{getGprsCommand()}</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setGprsMethod('sms')}>
                  <input type="radio" name="gprs" className="shrink-0" checked={gprsMethod === 'sms'} onChange={() => setGprsMethod('sms')} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">Send via SMS {!hasSim && '(no SIM linked)'}{smsCommandParts.length > 1 && <span className="text-xs text-muted-foreground font-normal"> — sent as {smsCommandParts.length} messages</span>}</p>
                    {smsCommandParts.map((part, i) => (
                      <p key={i} className="text-xs text-muted-foreground font-mono break-all">{smsCommandParts.length > 1 ? `SMS ${i + 1}: ` : ''}{part}</p>
                    ))}
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setGprsMethod('skip')}>
                  <input type="radio" name="gprs" checked={gprsMethod === 'skip'} onChange={() => setGprsMethod('skip')} />
                  <p className="text-sm">Skip</p>
                </label>
              </div>
            ) : (
              <p className="text-xs text-amber-600">GPRS disable not available for {device.protocol} protocol. Skipping.</p>
            )}
          </div>
        )}

        {/* Step 3: Remove allocations */}
        {step === 3 && (
          <div className="space-y-3" data-testid="wizard-step-3">
            <p className="text-sm font-medium">Remove device from allocated users?</p>
            <p className="text-xs text-muted-foreground">Currently allocated to {device.allocated_tenant_ids?.length || 0} user(s).</p>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setRemoveAllocations('remove_all')}>
                <input type="radio" name="alloc" checked={removeAllocations === 'remove_all'} onChange={() => setRemoveAllocations('remove_all')} />
                <div>
                  <p className="text-sm font-medium">Remove from all users</p>
                  <p className="text-xs text-muted-foreground">Users will no longer see this device</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={async () => {
                setRemoveAllocations('select');
                // Fetch user details for currently allocated users
                if (allocatedUserDetails.length === 0 && device.allocated_tenant_ids?.length > 0) {
                  try {
                    const res = await api.get('/users?limit=1000&include_sub_accounts=true');
                    const allU = res.data || [];
                    const allocated = allU.filter(u => device.allocated_tenant_ids.includes(u.id));
                    setAllocatedUserDetails(allocated);
                    setKeepUserIds(device.allocated_tenant_ids); // Start with all kept
                  } catch {}
                }
              }}>
                <input type="radio" name="alloc" checked={removeAllocations === 'select'} onChange={() => {}} />
                <div>
                  <p className="text-sm font-medium">Select users to keep or remove</p>
                  <p className="text-xs text-muted-foreground">Choose which users to keep allocated</p>
                </div>
              </label>
              {removeAllocations === 'select' && allocatedUserDetails.length > 0 && (
                <div className="max-h-[200px] overflow-y-auto border rounded-lg p-2 space-y-1">
                  <p className="text-xs text-muted-foreground px-1 pb-1">Uncheck users to remove their allocation:</p>
                  {allocatedUserDetails.map(u => (
                    <label key={u.id} className="flex items-center gap-2 p-1.5 hover:bg-muted rounded text-sm cursor-pointer">
                      <Checkbox
                        checked={keepUserIds.includes(u.id)}
                        onCheckedChange={(checked) => {
                          setKeepUserIds(prev => checked ? [...prev, u.id] : prev.filter(id => id !== u.id));
                        }}
                      />
                      {u.full_name || u.email}
                      <Badge variant="outline" className="text-[9px] ml-auto">{u.role?.replace('_', ' ')}</Badge>
                    </label>
                  ))}
                </div>
              )}
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setRemoveAllocations('skip')}>
                <input type="radio" name="alloc" checked={removeAllocations === 'skip'} onChange={() => setRemoveAllocations('skip')} />
                <p className="text-sm">Skip — keep all allocations</p>
              </label>
            </div>
          </div>
        )}

        {/* Step 4: Deactivate SIM */}
        {step === 4 && (
          <div className="space-y-3" data-testid="wizard-step-4">
            <p className="text-sm font-medium">Deactivate SIM card?</p>
            {hasSim ? (
              <>
                <p className="text-xs text-muted-foreground">SIM: {device.sim_iccid} ({device.sim_provider || 'Unknown provider'})</p>
                {gprsMethod !== 'skip' && (
                  <div className="p-2 bg-blue-50 dark:bg-blue-950 rounded text-xs text-blue-700 dark:text-blue-300">
                    The wizard will wait for device acknowledgement before deactivating the SIM, so the disable command has time to be delivered.
                  </div>
                )}
                <div className="space-y-2">
                  {isProtectedSimProvider && (
                    <div className="p-2 bg-amber-50 dark:bg-amber-950 rounded text-xs text-amber-700 dark:text-amber-300" data-testid="wizard-sim-protected-note">
                      {device.sim_provider} SIMs must not be deactivated via the provider API — their plans are managed by the provider. The SIM will be kept active.
                    </div>
                  )}
                  <label
                    className={`flex items-center gap-3 p-3 border rounded-lg ${isProtectedSimProvider ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'}`}
                    onClick={() => { if (!isProtectedSimProvider) setDeactivateSim('deactivate'); }}
                    data-testid="wizard-sim-deactivate-option"
                  >
                    <input type="radio" name="sim" disabled={isProtectedSimProvider} checked={deactivateSim === 'deactivate'} onChange={() => { if (!isProtectedSimProvider) setDeactivateSim('deactivate'); }} />
                    <div>
                      <p className="text-sm font-medium">Deactivate via provider API</p>
                      <p className="text-xs text-muted-foreground">{isProtectedSimProvider ? `Not available for ${device.sim_provider} SIM cards` : `Calls ${device.sim_provider || 'provider'} API to suspend the SIM`}</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setDeactivateSim('skip')}>
                    <input type="radio" name="sim" checked={deactivateSim === 'skip'} onChange={() => setDeactivateSim('skip')} />
                    <p className="text-sm">Skip — keep SIM active</p>
                  </label>
                </div>
              </>
            ) : (
              <p className="text-xs text-amber-600">No SIM card linked to this device. Skipping.</p>
            )}
          </div>
        )}

        {/* Step 5: Billing */}
        {step === 5 && (
          <div className="space-y-3" data-testid="wizard-step-5">
            <p className="text-sm font-medium">Remove from billing?</p>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setRemoveBilling('remove')}>
                <input type="radio" name="billing" checked={removeBilling === 'remove'} onChange={() => setRemoveBilling('remove')} />
                <div>
                  <p className="text-sm font-medium">Remove from next billing cycle</p>
                  <p className="text-xs text-muted-foreground">Device will not be charged</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setRemoveBilling('skip')}>
                <input type="radio" name="billing" checked={removeBilling === 'skip'} onChange={() => setRemoveBilling('skip')} />
                <p className="text-sm">Skip — keep in billing</p>
              </label>
            </div>
          </div>
        )}

        {/* Step 6: Review & Execute */}
        {step === 6 && !isExecutionPhase && (
          <div className="space-y-3" data-testid="wizard-step-6-summary">
            <p className="text-sm font-medium">Review — the following actions will be performed:</p>
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center gap-2 p-2 bg-muted rounded"><CheckCircle className="w-4 h-4 text-red-500 shrink-0" /> Deactivate device</div>
              {gprsMethod !== 'skip' && (
                <div className="flex items-center gap-2 p-2 bg-muted rounded"><Send className="w-4 h-4 text-blue-500 shrink-0" /> Send GPRS disable via {gprsMethod === 'sms' ? 'SMS' : 'GPRS'}</div>
              )}
              {gprsMethod !== 'skip' && deactivateSim === 'deactivate' && hasSim && (
                <div className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-950 rounded"><Clock className="w-4 h-4 text-blue-500 shrink-0" /> Wait for device acknowledgement before SIM deactivation</div>
              )}
              {removeAllocations === 'remove_all' && (
                <div className="flex items-center gap-2 p-2 bg-muted rounded"><CheckCircle className="w-4 h-4 text-amber-500 shrink-0" /> Remove all user allocations</div>
              )}
              {removeAllocations === 'select' && keepUserIds.length < (device.allocated_tenant_ids?.length || 0) && (
                <div className="flex items-center gap-2 p-2 bg-muted rounded"><CheckCircle className="w-4 h-4 text-amber-500 shrink-0" /> Remove {(device.allocated_tenant_ids?.length || 0) - keepUserIds.length} user(s), keep {keepUserIds.length}</div>
              )}
              {deactivateSim === 'deactivate' && hasSim && (
                <div className="flex items-center gap-2 p-2 bg-muted rounded"><Radio className="w-4 h-4 text-amber-500 shrink-0" /> Deactivate SIM card</div>
              )}
              {removeBilling === 'remove' && (
                <div className="flex items-center gap-2 p-2 bg-muted rounded"><CheckCircle className="w-4 h-4 text-amber-500 shrink-0" /> Remove from billing</div>
              )}
            </div>
          </div>
        )}

        {/* Step 6: Live Execution Log */}
        {step === 6 && isExecutionPhase && (
          <div className="space-y-2" data-testid="wizard-execution-log">
            <p className="text-sm font-medium">{executionDone ? 'Execution complete:' : 'Executing...'}</p>
            {executionLog.map(entry => (
              <ExecutionLogEntry key={entry.key} entry={entry} onAction={handleWaitAction} />
            ))}
          </div>
        )}

        </div>

        <DialogFooter className="gap-2">
          {step < totalSteps && (
            <>
              <Button variant="outline" onClick={handleClose} data-testid="wizard-cancel-btn">Cancel</Button>
              {step > 1 && <Button variant="outline" onClick={() => setStep(s => s - 1)} data-testid="wizard-back-btn"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>}
              <Button onClick={() => setStep(s => s + 1)} data-testid="wizard-next-btn">
                {step === 1 ? 'Continue' : 'Next'} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}
          {step === totalSteps && !isExecutionPhase && (
            <>
              <Button variant="outline" onClick={() => setStep(s => s - 1)} data-testid="wizard-back-btn"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button onClick={handleExecute} className="bg-red-600 hover:bg-red-700" data-testid="wizard-execute-btn">
                Confirm & Execute
              </Button>
            </>
          )}
          {step === totalSteps && isExecutionPhase && executionDone && (
            <Button onClick={handleClose} data-testid="wizard-done-btn">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Close Warning Dialog */}
      <Dialog open={showCloseWarning} onOpenChange={setShowCloseWarning}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Wizard Running
            </DialogTitle>
            <DialogDescription>
              The deactivation process is still running. Closing now will abort remaining steps. Are you sure?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCloseWarning(false)}>Continue Wizard</Button>
            <Button variant="destructive" onClick={() => { setShowCloseWarning(false); handleClose(); }}>Close & Abort</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};


// ═══════════════════════════════════════════════════════
// DEVICE REACTIVATION WIZARD
// ═══════════════════════════════════════════════════════

export const DeviceReactivationWizard = ({ open, onClose, device, api, users, onComplete }) => {
  const [step, setStep] = useState(1);

  // Configuration choices
  const [reactivateSim, setReactivateSim] = useState('skip');
  const [gprsMethod, setGprsMethod] = useState('skip');
  const [allocateUsers, setAllocateUsers] = useState('skip');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [allocatableUsers, setAllocatableUsers] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [addBilling, setAddBilling] = useState('skip');

  // Sync selectedUserIds when device changes (dialog opens with new device).
  // Intentionally keyed on device.id only — re-running on allocated_tenant_ids
  // changes would clobber the user's in-progress selection.
  React.useEffect(() => {
    if (device?.allocated_tenant_ids) {
      setSelectedUserIds(device.allocated_tenant_ids);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device?.id]);

  // Execution state
  const [isExecutionPhase, setIsExecutionPhase] = useState(false);
  const [executionLog, setExecutionLog] = useState([]);
  const [executionDone, setExecutionDone] = useState(false);
  const [showReconnectWarning, setShowReconnectWarning] = useState(false);
  const [showCloseWarning, setShowCloseWarning] = useState(false);
  const skipWaitRef = useRef(false);
  const abortRef = useRef(false);
  const waitResolverRef = useRef(null);

  const totalSteps = 6;
  const protocol = device?.protocol?.toLowerCase() || '';
  const pin = device?.device_password || '';
  const isMeitrack = protocol.includes('meitrack');
  const isTeltonika = protocol.includes('teltonika');
  const hasSim = !!(device?.sim_iccid);
  // 1NCE / Truphone (1GLOBAL) SIM plans are provider-managed — never toggle them via the provider API
  const isProtectedSimProvider = /truphone|1nce|1global/i.test(device?.sim_provider || '');

  const TELTONIKA_ENABLE_PARAMS = 'setparam 10000:120;10100:120;10200:120;10005:125;10105:125;10205:125;10050:30;10150:30;10250:30;10052:30;10152:30;10252:30;10055:32;10155:32;10255:32;2000:1';

  const getSmsEnableCommandParts = () => {
    if (isMeitrack) return [`${pin || '0000'},A21,1`];
    if (isTeltonika) return buildTeltonikaSmsParts(pin, TELTONIKA_ENABLE_PARAMS);
    return [];
  };
  const smsEnableParts = getSmsEnableCommandParts();

  const updateLog = useCallback((key, updates) => {
    setExecutionLog(prev => prev.map(e => e.key === key ? { ...e, ...updates } : e));
  }, []);

  // Countdown timer — wait N seconds with live countdown, skippable
  const waitCountdown = async (logKey, totalSecs, label) => {
    for (let elapsed = 0; elapsed < totalSecs; elapsed++) {
      if (skipWaitRef.current || abortRef.current) return 'skipped';
      const remaining = totalSecs - elapsed;
      updateLog(logKey, {
        status: 'waiting', showSkip: true,
        message: `${label} — ${remaining}s remaining`,
      });
      await sleep(1000);
    }
    return 'done';
  };

  // For reactivation: check that the carrier accepted/queued our outgoing SMS.
  // We DON'T wait for device reply — device may take 5-30 mins to come online.
  // M2M One keeps SMS pending until the device reconnects, so carrier acceptance = success.
  const pollSmsCarrierAccepted = async (simCardIdForPoll, sentCommandText, sentAtTime, maxSecs = 120) => {
    const maxAttempts = Math.ceil(maxSecs / 5);
    const sentTime = new Date(sentAtTime);
    for (let i = 0; i < maxAttempts; i++) {
      if (skipWaitRef.current || abortRef.current) return 'skipped';
      try {
        const res = await api.get(`/admin/sim-cards/${simCardIdForPoll}/sms/history`);
        const messages = Array.isArray(res.data) ? res.data : (res.data?.messages || []);
        for (const msg of messages) {
          const msgStatus = (msg.status || '').toLowerCase();
          const msgTime = msg.created_at ? new Date(msg.created_at) : null;
          // Check outgoing message accepted by carrier (delivered/pending/queued/sent/success)
          if (msg.direction === 'outgoing' && msgTime && msgTime >= sentTime) {
            const msgText = (msg.message || '').trim();
            const cmdText = (sentCommandText || '').trim();
            if (msgText.includes(cmdText) || cmdText.includes(msgText)) {
              if (msgStatus && msgStatus !== 'unknown') return 'queued';
            }
          }
          // Bonus: if device already replied, even better
          if (msg.direction === 'incoming' && msgTime && msgTime > sentTime) return 'delivered';
        }
      } catch {}
      updateLog('gprs_wait', {
        status: 'waiting', showSkip: true,
        message: `Checking carrier accepted SMS... Attempt ${i + 1}/${maxAttempts} — ${(i + 1) * 5}s elapsed`,
      });
      await sleep(5000);
    }
    return 'timeout';
  };

  // Send SMS (split into parts when >160 chars) and poll for carrier acceptance
  const sendSmsAndPoll = async (simCardId, smsParts) => {
    const sentAtTime = new Date().toISOString();
    try {
      for (let pi = 0; pi < smsParts.length; pi++) {
        updateLog('gprs_wait', { status: 'waiting', showSkip: true, message: `Sending SMS ${pi + 1}/${smsParts.length}: ${smsParts[pi]}` });
        await api.post(`/admin/sim-cards/${simCardId}/sms/send`, { message: smsParts[pi] });
        if (pi < smsParts.length - 1) await sleep(2000);
      }
      updateLog('gprs_wait', { status: 'waiting', showSkip: true, message: `${smsParts.length > 1 ? `${smsParts.length} SMS messages sent` : `SMS sent: ${smsParts[0]}`} — checking carrier accepted...` });
    } catch (e) {
      updateLog('gprs_wait', { status: 'failed', showSkip: false, message: `SMS send failed: ${e.response?.data?.detail || 'Error'}` });
      return 'failed';
    }
    return await pollSmsCarrierAccepted(simCardId, smsParts[smsParts.length - 1], sentAtTime, 120);
  };

  const handleExecute = async () => {
    setIsExecutionPhase(true);
    skipWaitRef.current = false;
    abortRef.current = false;

    const simActivated = reactivateSim === 'reactivate' && hasSim;
    const sendGprs = gprsMethod === 'sms' && (isTeltonika || isMeitrack);

    // Build execution plan
    const plan = [{ key: 'reactivate', label: 'Reactivate device', status: 'pending' }];
    if (simActivated) {
      plan.push({ key: 'sim', label: 'Reactivate SIM card', status: 'pending' });
      plan.push({ key: 'sim_wait', label: 'Wait for SIM provisioning', status: 'pending' });
    }
    if (sendGprs) {
      plan.push({ key: 'gprs', label: 'Send GPRS enable via SMS', status: 'pending' });
      plan.push({ key: 'gprs_wait', label: 'Confirm carrier accepted SMS', status: 'pending' });
    }
    if (allocateUsers === 'allocate' && selectedUserIds.length > 0) {
      plan.push({ key: 'allocations', label: `Allocate to ${selectedUserIds.length} user(s)`, status: 'pending' });
    }
    if (addBilling === 'add') {
      plan.push({ key: 'billing', label: 'Add to billing', status: 'pending' });
    }
    setExecutionLog(plan);
    await sleep(100);

    // 1. Reactivate device
    updateLog('reactivate', { status: 'running' });
    try {
      await api.put(`/admin/devices/${device.id}`, { is_active: true });
      updateLog('reactivate', { status: 'success', message: 'Device reactivated' });
    } catch (e) {
      updateLog('reactivate', { status: 'failed', message: e.response?.data?.detail || 'Failed' });
    }
    if (abortRef.current) return;

    // 2. Reactivate SIM
    let simCardId = null;
    if (simActivated) {
      updateLog('sim', { status: 'running' });
      try {
        const simRes = await api.get(`/admin/sim-cards/search-by-iccid/${encodeURIComponent(device.sim_iccid)}`);
        simCardId = simRes.data?.id;
        if (simCardId) {
          await api.put(`/admin/sim-cards/${simCardId}/status`, null, { params: { new_status: 'ACTIVATED' } });
          updateLog('sim', { status: 'success', message: 'SIM card reactivated via provider API' });
        }
      } catch (e) {
        updateLog('sim', { status: 'failed', message: e.response?.data?.detail || 'Failed to reactivate SIM' });
      }
      if (abortRef.current) return;

      // 2b. Wait 120s for SIM provisioning + device network reconnection
      updateLog('sim_wait', { status: 'waiting', showSkip: true, message: 'Waiting for SIM provisioning...' });
      skipWaitRef.current = false;
      const provisionResult = await waitCountdown('sim_wait', 120, 'SIM provisioning + device network reconnection');
      if (provisionResult === 'skipped') {
        updateLog('sim_wait', { status: 'skipped', showSkip: false, message: 'Provisioning wait skipped by admin' });
      } else {
        updateLog('sim_wait', { status: 'success', showSkip: false, message: 'SIM provisioning wait complete' });
      }
      skipWaitRef.current = false;
    }
    if (abortRef.current) return;

    // 3. Send GPRS enable via SMS + wait for confirmation
    if (sendGprs) {
      updateLog('gprs', { status: 'running' });
      const smsParts = getSmsEnableCommandParts();
      if (!simCardId && device.sim_iccid) {
        try {
          const simRes = await api.get(`/admin/sim-cards/search-by-iccid/${encodeURIComponent(device.sim_iccid)}`);
          simCardId = simRes.data?.id;
        } catch {}
      }

      if (simCardId && smsParts.length > 0) {
        updateLog('gprs', { status: 'success', message: smsParts.length > 1 ? `GPRS enable command (split into ${smsParts.length} SMS, each with its own setparam): ${smsParts.join('  |  ')}` : `GPRS enable command: ${smsParts[0]}` });

        // Send SMS and poll for carrier acceptance
        skipWaitRef.current = false;
        let smsResult = await sendSmsAndPoll(simCardId, smsParts);

        if (smsResult === 'queued' || smsResult === 'delivered') {
          updateLog('gprs_wait', { status: 'success', showSkip: false, message: smsResult === 'delivered' ? 'Device confirmed — GPRS enabled' : 'SMS accepted by carrier — queued for delivery when device comes online', actions: [] });
        } else if (smsResult === 'skipped') {
          updateLog('gprs_wait', { status: 'skipped', showSkip: false, message: 'Wait skipped by admin' });
        } else if (smsResult === 'timeout') {
          // Show simple retry options
          updateLog('gprs_wait', {
            status: 'timeout', showSkip: false,
            message: 'Carrier did not confirm SMS acceptance after 120s',
            actions: [
              { key: 'retry_sms', label: 'Retry', variant: 'default' },
              { key: 'skip', label: 'Skip', variant: 'outline' },
            ],
          });

          const decision = await new Promise(resolve => { waitResolverRef.current = resolve; });

          if (decision === 'retry_sms') {
            skipWaitRef.current = false;
            smsResult = await sendSmsAndPoll(simCardId, smsParts);
            if (smsResult === 'queued' || smsResult === 'delivered') {
              updateLog('gprs_wait', { status: 'success', showSkip: false, message: 'SMS accepted by carrier on retry', actions: [] });
            } else {
              updateLog('gprs_wait', { status: 'timeout', showSkip: false, message: 'SMS retry: carrier did not confirm — proceeding', actions: [] });
            }
          } else {
            updateLog('gprs_wait', { status: 'skipped', showSkip: false, message: 'Skipped — proceeding', actions: [] });
          }
        }
      } else {
        updateLog('gprs', { status: 'failed', message: 'No SIM card found or unsupported protocol' });
        updateLog('gprs_wait', { status: 'skipped', showSkip: false, message: 'Skipped — SMS not sent' });
      }
    }
    if (abortRef.current) return;

    // 4. Allocate users
    if (allocateUsers === 'allocate' && selectedUserIds.length > 0) {
      updateLog('allocations', { status: 'running' });
      try {
        const currentAlloc = device.allocated_tenant_ids || [];
        const merged = [...new Set([...currentAlloc, ...selectedUserIds])];
        await api.put(`/admin/devices/${device.id}/allocation`, { allocated_user_ids: merged });
        updateLog('allocations', { status: 'success', message: `Allocated to ${selectedUserIds.length} user(s)` });
      } catch (e) {
        updateLog('allocations', { status: 'failed', message: e.response?.data?.detail || 'Failed' });
      }
    }

    // 5. Billing
    if (addBilling === 'add') {
      updateLog('billing', { status: 'running' });
      try {
        await api.put(`/admin/devices/${device.id}`, { billing_excluded: false });
        updateLog('billing', { status: 'success', message: 'Added to billing' });
      } catch (e) {
        updateLog('billing', { status: 'failed', message: e.response?.data?.detail || 'Failed' });
      }
    }

    setExecutionDone(true);
    logWizardAudit(api, 'device_reactivation', 'device', device.id, device.name, executionLog);
  };

  const handleClose = () => {
    abortRef.current = true;
    skipWaitRef.current = true;
    waitResolverRef.current?.('skip');
    setStep(1);
    setReactivateSim('skip');
    setGprsMethod('skip');
    setAllocateUsers('skip');
    setSelectedUserIds([]);
    setAllocatableUsers(null);
    setUserSearch('');
    setAddBilling('skip');
    setExecutionLog([]);
    setIsExecutionPhase(false);
    setExecutionDone(false);
    setShowReconnectWarning(false);
    setShowCloseWarning(false);
    onClose();
    if (executionDone) onComplete?.();
  };

  const handleDialogClose = () => {
    if (isExecutionPhase && !executionDone) {
      setShowCloseWarning(true);
    } else {
      handleClose();
    }
  };

  const handleWaitAction = (action) => {
    if (action === 'skip_wait') {
      skipWaitRef.current = true;
    } else {
      waitResolverRef.current?.(action);
    }
  };

  if (!device) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleDialogClose(); }}>
      <DialogContent className="max-w-xl h-[620px] max-h-[90vh] flex flex-col" data-testid="device-reactivation-wizard" onPointerDownOutside={(e) => { if (isExecutionPhase && !executionDone) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Power className="w-5 h-5 text-green-500" />
            Reactivate Device
          </DialogTitle>
          <DialogDescription>
            {device.name} ({device.imei || device.mac_address})
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-1 mb-2">
          {Array.from({ length: totalSteps }, (_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full ${i + 1 <= step ? 'bg-green-500' : 'bg-muted'}`} />
          ))}
          <span className="text-xs text-muted-foreground ml-2">Step {step}/{totalSteps}</span>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-3">


        {/* Step 1: Confirm */}
        {step === 1 && (
          <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200" data-testid="wizard-step-1">
            <p className="text-sm font-medium">Reactivate {device.name}?</p>
            <p className="text-xs text-muted-foreground mt-1">The device will start connecting to the server again.</p>
          </div>
        )}

        {/* Step 2: Reactivate SIM */}
        {step === 2 && (
          <div className="space-y-3" data-testid="wizard-step-2">
            <p className="text-sm font-medium">Reactivate SIM card first?</p>
            <p className="text-xs text-muted-foreground">SIM must be active before the device can receive SMS commands.</p>
            {hasSim ? (
              <div className="space-y-2">
                {isProtectedSimProvider && (
                  <div className="p-2 bg-amber-50 dark:bg-amber-950 rounded text-xs text-amber-700 dark:text-amber-300" data-testid="wizard-sim-protected-note">
                    {device.sim_provider} SIMs must not be reactivated via the provider API — their plans are managed by the provider. This step will be skipped.
                  </div>
                )}
                <label
                  className={`flex items-center gap-3 p-3 border rounded-lg ${isProtectedSimProvider ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'}`}
                  onClick={() => { if (!isProtectedSimProvider) setReactivateSim('reactivate'); }}
                  data-testid="wizard-sim-reactivate-option"
                >
                  <input type="radio" name="sim" disabled={isProtectedSimProvider} checked={reactivateSim === 'reactivate'} onChange={() => { if (!isProtectedSimProvider) setReactivateSim('reactivate'); }} />
                  <div>
                    <p className="text-sm font-medium">Reactivate via provider API</p>
                    <p className="text-xs text-muted-foreground">{isProtectedSimProvider ? `Not available for ${device.sim_provider} SIM cards` : `SIM: ${device.sim_iccid}`}</p>
                    {!isProtectedSimProvider && gprsMethod === 'sms' && (
                      <p className="text-xs text-blue-600 mt-1">Will wait 120s for SIM provisioning before sending SMS</p>
                    )}
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setReactivateSim('skip')}>
                  <input type="radio" name="sim" checked={reactivateSim === 'skip'} onChange={() => setReactivateSim('skip')} />
                  <p className="text-sm">Skip — SIM is already active</p>
                </label>
              </div>
            ) : (
              <p className="text-xs text-amber-600">No SIM card linked. Skipping.</p>
            )}
          </div>
        )}

        {/* Step 3: GPRS enable via SMS */}
        {step === 3 && (
          <div className="space-y-3" data-testid="wizard-step-3">
            <p className="text-sm font-medium">Send GPRS enable command via SMS?</p>
            {(isTeltonika || isMeitrack) ? (
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setGprsMethod('sms')}>
                  <input type="radio" name="gprs" className="shrink-0" checked={gprsMethod === 'sms'} onChange={() => setGprsMethod('sms')} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">Send via SMS{smsEnableParts.length > 1 && <span className="text-xs text-muted-foreground font-normal"> — sent as {smsEnableParts.length} messages</span>}</p>
                    {smsEnableParts.map((part, i) => (
                      <p key={i} className="text-xs text-muted-foreground font-mono break-all">{smsEnableParts.length > 1 ? `SMS ${i + 1}: ` : ''}{part}</p>
                    ))}
                    <p className="text-xs text-blue-600 mt-1">Will confirm carrier accepted the SMS for delivery</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setGprsMethod('skip')}>
                  <input type="radio" name="gprs" checked={gprsMethod === 'skip'} onChange={() => setGprsMethod('skip')} />
                  <p className="text-sm">Skip — GPRS is already enabled</p>
                </label>
              </div>
            ) : (
              <p className="text-xs text-amber-600">Not available for {device.protocol}. Skipping.</p>
            )}
          </div>
        )}

        {/* Step 4: Allocate users */}
        {step === 4 && (
          <div className="space-y-3" data-testid="wizard-step-4">
            <p className="text-sm font-medium">Allocate device to user(s)?</p>
            <p className="text-xs text-muted-foreground">Currently allocated to {device?.allocated_tenant_ids?.length || 0} user(s). Users at their device limit are hidden unless already allocated.</p>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={async () => {
                setAllocateUsers('allocate');
                if (!allocatableUsers) {
                  try {
                    const res = await api.get('/admin/allocatable-users');
                    setAllocatableUsers(res.data || []);
                  } catch { setAllocatableUsers([]); }
                }
              }}>
                <input type="radio" name="alloc" checked={allocateUsers === 'allocate'} onChange={() => {}} />
                <p className="text-sm font-medium">Select users to allocate</p>
              </label>
              {allocateUsers === 'allocate' && allocatableUsers && (
                <>
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border rounded-md bg-background"
                    data-testid="user-search-input"
                  />
                  <div className="max-h-[200px] overflow-y-auto border rounded-lg p-2 space-y-1">
                    {allocatableUsers.filter(u => {
                      if (['sub_account'].includes(u.role)) return false;
                      const isAllocated = (device?.allocated_tenant_ids || []).includes(u.id);
                      if (isAllocated) return true;
                      const max = u.max_devices ?? 0;
                      const count = u.device_count ?? 0;
                      // Hide users with 0 quota or at/over quota
                      if (max !== -1 && count >= max) return false;
                      return true;
                    }).filter(u => {
                      if (!userSearch.trim()) return true;
                      const q = userSearch.toLowerCase();
                      return (u.full_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                    }).map(u => {
                      const isAllocated = (device?.allocated_tenant_ids || []).includes(u.id);
                      const max = u.max_devices ?? 0;
                      const count = u.device_count ?? 0;
                      const quotaLabel = max === -1 ? `${count}/unlimited` : `${count}/${max}`;
                      return (
                        <label key={u.id} className="flex items-center gap-2 p-1.5 hover:bg-muted rounded text-sm cursor-pointer">
                          <Checkbox
                            checked={selectedUserIds.includes(u.id)}
                            onCheckedChange={(checked) => {
                              setSelectedUserIds(prev => checked ? [...prev, u.id] : prev.filter(id => id !== u.id));
                            }}
                          />
                          <span className="truncate">{u.full_name || u.email}</span>
                          {isAllocated && <Badge className="text-[9px] bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 shrink-0">Allocated</Badge>}
                          <span className="text-[10px] text-muted-foreground ml-auto shrink-0">{quotaLabel}</span>
                          <Badge variant="outline" className="text-[9px] shrink-0">{u.role?.replace('_', ' ')}</Badge>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setAllocateUsers('skip')}>
                <input type="radio" name="alloc" checked={allocateUsers === 'skip'} onChange={() => setAllocateUsers('skip')} />
                <p className="text-sm">Skip — keep current allocations</p>
              </label>
            </div>
          </div>
        )}

        {/* Step 5: Billing */}
        {step === 5 && (
          <div className="space-y-3" data-testid="wizard-step-5">
            <p className="text-sm font-medium">Add to billing?</p>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setAddBilling('add')}>
                <input type="radio" name="billing" checked={addBilling === 'add'} onChange={() => setAddBilling('add')} />
                <p className="text-sm font-medium">Add to billing cycle</p>
              </label>
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setAddBilling('skip')}>
                <input type="radio" name="billing" checked={addBilling === 'skip'} onChange={() => setAddBilling('skip')} />
                <p className="text-sm">Skip</p>
              </label>
            </div>
          </div>
        )}

        {/* Step 6: Review Summary (pre-execution) */}
        {step === 6 && !isExecutionPhase && (
          <div className="space-y-3" data-testid="wizard-step-6-summary">
            <p className="text-sm font-medium">Review — the following actions will be performed:</p>
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center gap-2 p-2 bg-muted rounded"><CheckCircle className="w-4 h-4 text-green-500 shrink-0" /> Reactivate device</div>
              {reactivateSim === 'reactivate' && hasSim && (
                <>
                  <div className="flex items-center gap-2 p-2 bg-muted rounded"><Radio className="w-4 h-4 text-green-500 shrink-0" /> Reactivate SIM card</div>
                  <div className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-950 rounded"><Clock className="w-4 h-4 text-blue-500 shrink-0" /> Wait 120s for SIM provisioning</div>
                </>
              )}
              {gprsMethod === 'sms' && (isTeltonika || isMeitrack) && (
                <>
                  <div className="flex items-center gap-2 p-2 bg-muted rounded"><Send className="w-4 h-4 text-blue-500 shrink-0" /> Send GPRS enable via SMS</div>
                  <div className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-950 rounded"><Clock className="w-4 h-4 text-blue-500 shrink-0" /> Poll carrier accepted SMS (queued for delivery)</div>
                </>
              )}
              {allocateUsers === 'allocate' && selectedUserIds.length > 0 && (
                <div className="flex items-center gap-2 p-2 bg-muted rounded"><CheckCircle className="w-4 h-4 text-blue-500 shrink-0" /> Allocate to {selectedUserIds.length} user(s)</div>
              )}
              {addBilling === 'add' && (
                <div className="flex items-center gap-2 p-2 bg-muted rounded"><CheckCircle className="w-4 h-4 text-blue-500 shrink-0" /> Add to billing</div>
              )}
            </div>
          </div>
        )}

        {/* Step 6: Live Execution Log */}
        {step === 6 && isExecutionPhase && (
          <div className="space-y-2" data-testid="wizard-execution-log">
            <p className="text-sm font-medium">{executionDone ? 'Execution complete:' : 'Executing...'}</p>
            {executionLog.map(entry => (
              <ExecutionLogEntry key={entry.key} entry={entry} onAction={handleWaitAction} />
            ))}
          </div>
        )}

        </div>

        <DialogFooter className="gap-2">
          {step < totalSteps && (
            <>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              {step > 1 && <Button variant="outline" onClick={() => setStep(s => s - 1)}><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>}
              <Button onClick={() => setStep(s => s + 1)}>
                {step === 1 ? 'Continue' : 'Next'} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}
          {step === totalSteps && !isExecutionPhase && (
            <>
              <Button variant="outline" onClick={() => setStep(s => s - 1)}><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button onClick={handleExecute} className="bg-green-600 hover:bg-green-700" data-testid="wizard-execute-btn">
                Confirm & Execute
              </Button>
            </>
          )}
          {step === totalSteps && isExecutionPhase && executionDone && (
            <Button onClick={() => setShowReconnectWarning(true)} data-testid="wizard-done-btn">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Reconnect Warning Dialog */}
      <Dialog open={showReconnectWarning} onOpenChange={setShowReconnectWarning}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Device Reconnection Notice
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-2 text-sm">
              <p>The device may take <strong>up to 30 minutes</strong> to reconnect to the server after SIM reactivation.</p>
              <p>If it doesn't reconnect within that time, please investigate further:</p>
              <ul className="list-disc pl-4 space-y-1 text-xs text-muted-foreground">
                <li>Check the device has power and antenna connected</li>
                <li>Verify the SIM card is active in the provider portal</li>
                <li>The device may have crashed — try sending a <strong>CPU Reset</strong> command via SMS</li>
                <li>Check the device's APN settings are correct</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => { setShowReconnectWarning(false); handleClose(); }}>
              Understood
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Warning Dialog */}
      <Dialog open={showCloseWarning} onOpenChange={setShowCloseWarning}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Wizard Running
            </DialogTitle>
            <DialogDescription>
              The reactivation process is still running. Closing now will abort remaining steps. Are you sure?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCloseWarning(false)}>Continue Wizard</Button>
            <Button variant="destructive" onClick={() => { setShowCloseWarning(false); handleClose(); }}>Close & Abort</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};


// ═══════════════════════════════════════════════════════
// USER DEACTIVATION WIZARD
// ═══════════════════════════════════════════════════════

export const UserDeactivationWizard = ({ open, onClose, user, api, devices, allUsers, onOpenDeviceWizard, onComplete }) => {
  const [step, setStep] = useState(1);

  // Configuration choices
  const [removeAllocations, setRemoveAllocations] = useState('skip');
  const [transferToUserIds, setTransferToUserIds] = useState([]);
  const [transferAllocatableUsers, setTransferAllocatableUsers] = useState(null);
  const [transferUserSearch, setTransferUserSearch] = useState('');
  const [deactivateSubs, setDeactivateSubs] = useState('skip');
  const [stopBilling, setStopBilling] = useState('skip');

  // Execution state
  const [isExecutionPhase, setIsExecutionPhase] = useState(false);
  const [executionLog, setExecutionLog] = useState([]);
  const [executionDone, setExecutionDone] = useState(false);
  const [showCloseWarning, setShowCloseWarning] = useState(false);

  const totalSteps = 6;
  const userDevices = (devices || []).filter(d => d.allocated_tenant_ids?.includes(user?.id));

  const updateLog = useCallback((key, updates) => {
    setExecutionLog(prev => prev.map(e => e.key === key ? { ...e, ...updates } : e));
  }, []);

  const handleExecute = async () => {
    setIsExecutionPhase(true);

    const plan = [{ key: 'deactivate', label: 'Deactivate user account', status: 'pending' }];
    if (removeAllocations === 'remove_all') {
      plan.push({ key: 'allocations', label: 'Remove all device allocations', status: 'pending' });
    } else if (removeAllocations === 'transfer' && transferToUserIds.length > 0) {
      plan.push({ key: 'allocations', label: `Transfer devices to ${transferToUserIds.length} user(s)`, status: 'pending' });
    }
    if (deactivateSubs === 'deactivate_all') {
      plan.push({ key: 'sub_accounts', label: 'Deactivate sub-accounts', status: 'pending' });
    }
    if (stopBilling === 'stop') {
      plan.push({ key: 'billing', label: 'Stop billing for all devices', status: 'pending' });
    }
    setExecutionLog(plan);
    await sleep(100);

    // 1. Deactivate user
    updateLog('deactivate', { status: 'running' });
    try {
      await api.put(`/users/${user.id}`, { disabled: true, is_active: false });
      updateLog('deactivate', { status: 'success', message: 'User account deactivated' });
    } catch (e) {
      updateLog('deactivate', { status: 'failed', message: e.response?.data?.detail || 'Failed' });
    }

    // 2. Remove/transfer allocations
    if (removeAllocations === 'remove_all') {
      updateLog('allocations', { status: 'running' });
      try {
        // Remove deactivated user from each device's allocation (syncs both sides)
        for (const dev of userDevices) {
          const newAlloc = (dev.allocated_tenant_ids || []).filter(uid => uid !== user.id);
          await api.put(`/admin/devices/${dev.id}/allocation`, {
            allocated_user_ids: newAlloc,
            ...(newAlloc.length === 0 ? { confirm_remove_all: true } : {}),
          });
        }
        updateLog('allocations', { status: 'success', message: `Removed user from ${userDevices.length} device(s)` });
      } catch (e) {
        updateLog('allocations', { status: 'failed', message: e.response?.data?.detail || 'Failed' });
      }
    } else if (removeAllocations === 'transfer' && transferToUserIds.length > 0) {
      updateLog('allocations', { status: 'running' });
      try {
        let updated = 0;
        for (const dev of userDevices) {
          // Remove deactivated user, add all selected transfer targets
          const currentAlloc = (dev.allocated_tenant_ids || []).filter(uid => uid !== user.id);
          const newAlloc = [...new Set([...currentAlloc, ...transferToUserIds])];
          try {
            await api.put(`/admin/devices/${dev.id}/allocation`, { allocated_user_ids: newAlloc });
            updated++;
          } catch {}
        }
        updateLog('allocations', { status: 'success', message: `Updated allocations for ${updated} device(s)` });
      } catch (e) {
        updateLog('allocations', { status: 'failed', message: e.response?.data?.detail || 'Failed to transfer' });
      }
    }

    // 3. Deactivate sub-accounts
    if (deactivateSubs === 'deactivate_all') {
      updateLog('sub_accounts', { status: 'running' });
      try {
        const subRes = await api.get('/users?limit=1000&include_sub_accounts=true');
        const subAccounts = (subRes.data || []).filter(u => u.parent_user_id === user.id || u.manager_id === user.id);
        let deactivated = 0;
        for (const sub of subAccounts) {
          try {
            await api.put(`/users/${sub.id}`, { disabled: true, is_active: false });
            deactivated++;
          } catch {}
        }
        updateLog('sub_accounts', { status: 'success', message: `Deactivated ${deactivated} sub-account(s)` });
      } catch (e) {
        updateLog('sub_accounts', { status: 'failed', message: e.response?.data?.detail || 'Failed' });
      }
    }

    // 4. Stop billing for user's devices
    if (stopBilling === 'stop') {
      updateLog('billing', { status: 'running' });
      try {
        let updated = 0;
        for (const dev of userDevices) {
          try {
            await api.put(`/admin/devices/${dev.id}`, { billing_excluded: true });
            updated++;
          } catch {}
        }
        updateLog('billing', { status: 'success', message: `Removed ${updated} device(s) from billing` });
      } catch (e) {
        updateLog('billing', { status: 'failed', message: e.response?.data?.detail || 'Failed' });
      }
    }

    setExecutionDone(true);
    logWizardAudit(api, 'user_deactivation', 'user', user.id, user.full_name, executionLog);
  };

  const handleClose = () => {
    setStep(1);
    setRemoveAllocations('skip');
    setTransferToUserIds([]);
    setTransferAllocatableUsers(null);
    setTransferUserSearch('');
    setDeactivateSubs('skip');
    setStopBilling('skip');
    setExecutionLog([]);
    setIsExecutionPhase(false);
    setExecutionDone(false);
    setShowCloseWarning(false);
    onClose();
    if (executionDone) onComplete?.();
  };

  const handleDialogClose = () => {
    if (isExecutionPhase && !executionDone) {
      setShowCloseWarning(true);
    } else {
      handleClose();
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleDialogClose(); }}>
      <DialogContent className="max-w-xl max-h-[90vh]" data-testid="user-deactivation-wizard" onPointerDownOutside={(e) => { if (isExecutionPhase && !executionDone) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Power className="w-5 h-5 text-red-500" />
            Deactivate User
          </DialogTitle>
          <DialogDescription>
            {userDisplayName(user)} ({user.email})
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-1 mb-2">
          {Array.from({ length: totalSteps }, (_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full ${i + 1 <= step ? 'bg-red-500' : 'bg-muted'}`} />
          ))}
          <span className="text-xs text-muted-foreground ml-2">Step {step}/{totalSteps}</span>
        </div>

        {/* Step 1: Confirm */}
        {step === 1 && (
          <div className="p-4 bg-red-50 dark:bg-red-950 rounded-lg border border-red-200" data-testid="wizard-step-1">
            <p className="text-sm font-medium">Deactivate {userDisplayName(user)}?</p>
            <p className="text-xs text-muted-foreground mt-1">The user will be unable to log in. {userDevices.length} device(s) allocated.</p>
          </div>
        )}

        {/* Step 2: Deactivate devices */}
        {step === 2 && (
          <div className="space-y-3" data-testid="wizard-step-2">
            <p className="text-sm font-medium">Deactivate user's devices? ({userDevices.length} device(s))</p>
            {userDevices.length > 0 ? (
              <div className="max-h-[250px] overflow-y-auto border rounded-lg p-2 space-y-1">
                {userDevices.map(d => (
                  <div key={d.id} className="flex items-center justify-between p-2 hover:bg-muted rounded text-sm">
                    <div>
                      <p className="font-medium">{d.name}</p>
                      <p className="text-xs text-muted-foreground">{d.imei || d.mac_address}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-[10px] ${d.is_active ? 'text-green-600' : 'text-red-600'}`}>
                        {d.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      {d.is_active && (
                        <Button size="sm" variant="outline" className="h-6 text-xs text-red-600" onClick={() => onOpenDeviceWizard?.(d)}>
                          Deactivate
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No devices allocated to this user.</p>
            )}
          </div>
        )}

        {/* Step 3: Remove allocations */}
        {step === 3 && (
          <div className="space-y-3" data-testid="wizard-step-3">
            <p className="text-sm font-medium">Remove device allocations?</p>
            <p className="text-xs text-muted-foreground">{userDevices.length} device(s) allocated to this user.</p>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setRemoveAllocations('remove_all')}>
                <input type="radio" name="alloc" checked={removeAllocations === 'remove_all'} onChange={() => setRemoveAllocations('remove_all')} />
                <p className="text-sm font-medium">Remove all allocations</p>
              </label>
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={async () => {
                setRemoveAllocations('transfer');
                if (!transferAllocatableUsers) {
                  try {
                    const res = await api.get('/admin/allocatable-users');
                    setTransferAllocatableUsers(res.data || []);
                    // Pre-select users already allocated to the deactivated user's devices (excluding the deactivated user)
                    const alreadyAllocated = new Set();
                    for (const dev of userDevices) {
                      for (const uid of (dev.allocated_tenant_ids || [])) {
                        if (uid !== user?.id) alreadyAllocated.add(uid);
                      }
                    }
                    setTransferToUserIds([...alreadyAllocated]);
                  } catch { setTransferAllocatableUsers([]); }
                }
              }}>
                <input type="radio" name="alloc" checked={removeAllocations === 'transfer'} onChange={() => {}} />
                <p className="text-sm font-medium">Transfer devices to another user</p>
              </label>
              {removeAllocations === 'transfer' && transferAllocatableUsers && (
                <>
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={transferUserSearch}
                    onChange={(e) => setTransferUserSearch(e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border rounded-md bg-background"
                  />
                  <div className="max-h-[200px] overflow-y-auto border rounded-lg p-2 space-y-1">
                    {transferAllocatableUsers.filter(u => {
                      if (u.id === user?.id) return false;
                      if (['sub_account'].includes(u.role)) return false;
                      const hasDevices = userDevices.some(d => (d.allocated_tenant_ids || []).includes(u.id));
                      if (hasDevices) return true;
                      const max = u.max_devices ?? 0;
                      const count = u.device_count ?? 0;
                      if (max !== -1 && count >= max) return false;
                      return true;
                    }).filter(u => {
                      if (!transferUserSearch.trim()) return true;
                      const q = transferUserSearch.toLowerCase();
                      return (u.full_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                    }).map(u => {
                      const hasDevices = userDevices.some(d => (d.allocated_tenant_ids || []).includes(u.id));
                      const max = u.max_devices ?? 0;
                      const count = u.device_count ?? 0;
                      const quotaLabel = max === -1 ? `${count}/unlimited` : `${count}/${max}`;
                      return (
                        <label key={u.id} className="flex items-center gap-2 p-1.5 hover:bg-muted rounded text-sm cursor-pointer">
                          <Checkbox
                            checked={transferToUserIds.includes(u.id)}
                            onCheckedChange={(checked) => {
                              setTransferToUserIds(prev => checked ? [...prev, u.id] : prev.filter(id => id !== u.id));
                            }}
                          />
                          <span className="truncate">{u.full_name || u.email}</span>
                          {hasDevices && <Badge className="text-[9px] bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 shrink-0">Allocated</Badge>}
                          <span className="text-[10px] text-muted-foreground ml-auto shrink-0">{quotaLabel}</span>
                          <Badge variant="outline" className="text-[9px] shrink-0">{u.role?.replace('_', ' ')}</Badge>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setRemoveAllocations('skip')}>
                <input type="radio" name="alloc" checked={removeAllocations === 'skip'} onChange={() => setRemoveAllocations('skip')} />
                <p className="text-sm">Skip — keep allocations</p>
              </label>
            </div>
          </div>
        )}

        {/* Step 4: Deactivate sub-accounts */}
        {step === 4 && (
          <div className="space-y-3" data-testid="wizard-step-4">
            <p className="text-sm font-medium">Deactivate sub-accounts?</p>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setDeactivateSubs('deactivate_all')}>
                <input type="radio" name="subs" checked={deactivateSubs === 'deactivate_all'} onChange={() => setDeactivateSubs('deactivate_all')} />
                <p className="text-sm font-medium">Deactivate all sub-accounts</p>
              </label>
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setDeactivateSubs('keep')}>
                <input type="radio" name="subs" checked={deactivateSubs === 'keep'} onChange={() => setDeactivateSubs('keep')} />
                <p className="text-sm">Keep sub-accounts active</p>
              </label>
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setDeactivateSubs('skip')}>
                <input type="radio" name="subs" checked={deactivateSubs === 'skip'} onChange={() => setDeactivateSubs('skip')} />
                <p className="text-sm">Skip</p>
              </label>
            </div>
          </div>
        )}

        {/* Step 5: Stop billing */}
        {step === 5 && (
          <div className="space-y-3" data-testid="wizard-step-5">
            <p className="text-sm font-medium">Stop billing for this user's devices?</p>
            <p className="text-xs text-muted-foreground">{userDevices.length} device(s) allocated — removing from billing will stop new invoices being generated.</p>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setStopBilling('stop')}>
                <input type="radio" name="billing" checked={stopBilling === 'stop'} onChange={() => setStopBilling('stop')} />
                <div>
                  <p className="text-sm font-medium">Stop billing for all devices</p>
                  <p className="text-xs text-muted-foreground">No new invoices will be created for {userDevices.length} device(s)</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setStopBilling('skip')}>
                <input type="radio" name="billing" checked={stopBilling === 'skip'} onChange={() => setStopBilling('skip')} />
                <p className="text-sm">Skip — keep billing active</p>
              </label>
            </div>
          </div>
        )}

        {/* Step 6: Review Summary (pre-execution) */}
        {step === 6 && !isExecutionPhase && (
          <div className="space-y-3" data-testid="wizard-step-6-summary">
            <p className="text-sm font-medium">Review — the following actions will be performed:</p>
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center gap-2 p-2 bg-muted rounded"><CheckCircle className="w-4 h-4 text-red-500 shrink-0" /> Deactivate user account</div>
              {removeAllocations === 'remove_all' && (
                <div className="flex items-center gap-2 p-2 bg-muted rounded"><CheckCircle className="w-4 h-4 text-amber-500 shrink-0" /> Remove all device allocations</div>
              )}
              {removeAllocations === 'transfer' && transferToUserIds.length > 0 && (
                <div className="flex items-center gap-2 p-2 bg-muted rounded"><CheckCircle className="w-4 h-4 text-blue-500 shrink-0" /> Transfer devices to {transferToUserIds.length} user(s)</div>
              )}
              {deactivateSubs === 'deactivate_all' && (
                <div className="flex items-center gap-2 p-2 bg-muted rounded"><CheckCircle className="w-4 h-4 text-amber-500 shrink-0" /> Deactivate all sub-accounts</div>
              )}
              {stopBilling === 'stop' && (
                <div className="flex items-center gap-2 p-2 bg-muted rounded"><CheckCircle className="w-4 h-4 text-amber-500 shrink-0" /> Stop billing for {userDevices.length} device(s)</div>
              )}
            </div>
          </div>
        )}

        {/* Step 6: Live Execution Log */}
        {step === 6 && isExecutionPhase && (
          <div className="space-y-2" data-testid="wizard-execution-log">
            <p className="text-sm font-medium">{executionDone ? 'Execution complete:' : 'Executing...'}</p>
            {executionLog.map(entry => (
              <ExecutionLogEntry key={entry.key} entry={entry} />
            ))}
          </div>
        )}

        <DialogFooter className="gap-2">
          {step < totalSteps && (
            <>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              {step > 1 && <Button variant="outline" onClick={() => setStep(s => s - 1)}><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>}
              <Button onClick={() => setStep(s => s + 1)}>
                {step === 1 ? 'Continue' : 'Next'} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}
          {step === totalSteps && !isExecutionPhase && (
            <>
              <Button variant="outline" onClick={() => setStep(s => s - 1)}><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button onClick={handleExecute} className="bg-red-600 hover:bg-red-700" data-testid="wizard-execute-btn">
                Confirm & Execute
              </Button>
            </>
          )}
          {step === totalSteps && isExecutionPhase && executionDone && (
            <Button onClick={handleClose} data-testid="wizard-done-btn">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Close Warning Dialog */}
      <Dialog open={showCloseWarning} onOpenChange={setShowCloseWarning}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Wizard Running
            </DialogTitle>
            <DialogDescription>
              The deactivation process is still running. Closing now will abort remaining steps. Are you sure?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCloseWarning(false)}>Continue Wizard</Button>
            <Button variant="destructive" onClick={() => { setShowCloseWarning(false); handleClose(); }}>Close & Abort</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};


// ═══════════════════════════════════════════════════════
// USER REACTIVATION WIZARD
// ═══════════════════════════════════════════════════════

export const UserReactivationWizard = ({ open, onClose, user, api, devices, allUsers, onOpenDeviceReactivationWizard, onComplete }) => {
  const [step, setStep] = useState(1);

  // Configuration choices
  const [reactivateSubs, setReactivateSubs] = useState('skip');
  const [addBilling, setAddBilling] = useState('skip');

  // Execution state
  const [isExecutionPhase, setIsExecutionPhase] = useState(false);
  const [executionLog, setExecutionLog] = useState([]);
  const [executionDone, setExecutionDone] = useState(false);
  const [showCloseWarning, setShowCloseWarning] = useState(false);

  const totalSteps = 5;
  const userDevices = (devices || []).filter(d => d.allocated_tenant_ids?.includes(user?.id));
  const inactiveDevices = userDevices.filter(d => !d.is_active);
  const billingExcluded = userDevices.filter(d => d.billing_excluded);

  const updateLog = useCallback((key, updates) => {
    setExecutionLog(prev => prev.map(e => e.key === key ? { ...e, ...updates } : e));
  }, []);

  const handleExecute = async () => {
    setIsExecutionPhase(true);

    const plan = [{ key: 'reactivate', label: 'Reactivate user account', status: 'pending' }];
    if (reactivateSubs === 'reactivate_all') {
      plan.push({ key: 'sub_accounts', label: 'Reactivate sub-accounts', status: 'pending' });
    }
    if (addBilling === 'add') {
      plan.push({ key: 'billing', label: 'Re-enable billing for devices', status: 'pending' });
    }
    setExecutionLog(plan);
    await sleep(100);

    // 1. Reactivate user
    updateLog('reactivate', { status: 'running' });
    try {
      await api.put(`/users/${user.id}`, { is_active: true, disabled: false });
      updateLog('reactivate', { status: 'success', message: 'User account reactivated' });
    } catch (e) {
      updateLog('reactivate', { status: 'failed', message: e.response?.data?.detail || 'Failed' });
    }

    // 2. Reactivate sub-accounts
    if (reactivateSubs === 'reactivate_all') {
      updateLog('sub_accounts', { status: 'running' });
      try {
        const subRes = await api.get('/users?limit=1000&include_sub_accounts=true');
        const subAccounts = (subRes.data || []).filter(u => u.parent_user_id === user.id || u.manager_id === user.id);
        const blockedSubs = subAccounts.filter(s => s.is_active === false || s.disabled);
        let reactivated = 0;
        for (const sub of blockedSubs) {
          try {
            await api.put(`/users/${sub.id}`, { is_active: true, disabled: false });
            reactivated++;
          } catch {}
        }
        updateLog('sub_accounts', { status: 'success', message: `Reactivated ${reactivated} sub-account(s)` });
      } catch (e) {
        updateLog('sub_accounts', { status: 'failed', message: e.response?.data?.detail || 'Failed' });
      }
    }

    // 4. Re-enable billing
    if (addBilling === 'add') {
      updateLog('billing', { status: 'running' });
      try {
        let updated = 0;
        for (const dev of billingExcluded) {
          try {
            await api.put(`/admin/devices/${dev.id}`, { billing_excluded: false });
            updated++;
          } catch {}
        }
        updateLog('billing', { status: 'success', message: `Re-enabled billing for ${updated} device(s)` });
      } catch (e) {
        updateLog('billing', { status: 'failed', message: e.response?.data?.detail || 'Failed' });
      }
    }

    setExecutionDone(true);
    logWizardAudit(api, 'user_reactivation', 'user', user.id, user.full_name, executionLog);
  };

  const handleClose = () => {
    setStep(1);
    setReactivateSubs('skip');
    setAddBilling('skip');
    setExecutionLog([]);
    setIsExecutionPhase(false);
    setExecutionDone(false);
    setShowCloseWarning(false);
    onClose();
    if (executionDone) onComplete?.();
  };

  const handleDialogClose = () => {
    if (isExecutionPhase && !executionDone) {
      setShowCloseWarning(true);
    } else {
      handleClose();
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleDialogClose(); }}>
      <DialogContent className="max-w-xl max-h-[90vh]" data-testid="user-reactivation-wizard" onPointerDownOutside={(e) => { if (isExecutionPhase && !executionDone) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Power className="w-5 h-5 text-green-500" />
            Reactivate User
          </DialogTitle>
          <DialogDescription>
            {userDisplayName(user)} ({user.email})
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-1 mb-2">
          {Array.from({ length: totalSteps }, (_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full ${i + 1 <= step ? 'bg-green-500' : 'bg-muted'}`} />
          ))}
          <span className="text-xs text-muted-foreground ml-2">Step {step}/{totalSteps}</span>
        </div>

        {/* Step 1: Confirm */}
        {step === 1 && (
          <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200" data-testid="wizard-step-1">
            <p className="text-sm font-medium">Reactivate {userDisplayName(user)}?</p>
            <p className="text-xs text-muted-foreground mt-1">The user will be able to log in again. {userDevices.length} device(s) allocated, {inactiveDevices.length} inactive.</p>
          </div>
        )}

        {/* Step 2: Reactivate devices */}
        {step === 2 && (
          <div className="space-y-3" data-testid="wizard-step-2">
            <p className="text-sm font-medium">Reactivate user's devices? ({userDevices.length} device(s), {inactiveDevices.length} inactive)</p>
            {userDevices.length > 0 ? (
              <div className="max-h-[250px] overflow-y-auto border rounded-lg p-2 space-y-1">
                {userDevices.map(d => (
                  <div key={d.id} className="flex items-center justify-between p-2 hover:bg-muted rounded text-sm">
                    <div>
                      <p className="font-medium">{d.name}</p>
                      <p className="text-xs text-muted-foreground">{d.imei || d.mac_address}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-[10px] ${d.is_active ? 'text-green-600' : 'text-red-600'}`}>
                        {d.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      {!d.is_active && (
                        <Button size="sm" variant="outline" className="h-6 text-xs text-green-600" onClick={() => onOpenDeviceReactivationWizard?.(d)}>
                          Reactivate
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No devices allocated to this user.</p>
            )}
          </div>
        )}

        {/* Step 3: Reactivate sub-accounts */}
        {step === 3 && (
          <div className="space-y-3" data-testid="wizard-step-3">
            <p className="text-sm font-medium">Reactivate sub-accounts?</p>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setReactivateSubs('reactivate_all')}>
                <input type="radio" name="subs" checked={reactivateSubs === 'reactivate_all'} onChange={() => setReactivateSubs('reactivate_all')} />
                <p className="text-sm font-medium">Reactivate all deactivated sub-accounts</p>
              </label>
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setReactivateSubs('skip')}>
                <input type="radio" name="subs" checked={reactivateSubs === 'skip'} onChange={() => setReactivateSubs('skip')} />
                <p className="text-sm">Skip</p>
              </label>
            </div>
          </div>
        )}

        {/* Step 4: Re-enable billing */}
        {step === 4 && (
          <div className="space-y-3" data-testid="wizard-step-4">
            <p className="text-sm font-medium">Re-enable billing for devices?</p>
            {billingExcluded.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">{billingExcluded.length} device(s) currently excluded from billing.</p>
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setAddBilling('add')}>
                  <input type="radio" name="billing" checked={addBilling === 'add'} onChange={() => setAddBilling('add')} />
                  <div>
                    <p className="text-sm font-medium">Re-enable billing</p>
                    <p className="text-xs text-muted-foreground">Invoices will resume for {billingExcluded.length} device(s)</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => setAddBilling('skip')}>
                  <input type="radio" name="billing" checked={addBilling === 'skip'} onChange={() => setAddBilling('skip')} />
                  <p className="text-sm">Skip — keep billing paused</p>
                </label>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No devices are excluded from billing. Skipping.</p>
            )}
          </div>
        )}

        {/* Step 5: Review Summary */}
        {step === 5 && !isExecutionPhase && (
          <div className="space-y-3" data-testid="wizard-step-5-summary">
            <p className="text-sm font-medium">Review — the following actions will be performed:</p>
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center gap-2 p-2 bg-muted rounded"><CheckCircle className="w-4 h-4 text-green-500 shrink-0" /> Reactivate user account</div>
              {reactivateSubs === 'reactivate_all' && (
                <div className="flex items-center gap-2 p-2 bg-muted rounded"><CheckCircle className="w-4 h-4 text-green-500 shrink-0" /> Reactivate sub-accounts</div>
              )}
              {addBilling === 'add' && (
                <div className="flex items-center gap-2 p-2 bg-muted rounded"><CheckCircle className="w-4 h-4 text-green-500 shrink-0" /> Re-enable billing for {billingExcluded.length} device(s)</div>
              )}
            </div>
          </div>
        )}

        {/* Step 5: Live Execution Log */}
        {step === 5 && isExecutionPhase && (
          <div className="space-y-2" data-testid="wizard-execution-log">
            <p className="text-sm font-medium">{executionDone ? 'Execution complete:' : 'Executing...'}</p>
            {executionLog.map(entry => (
              <ExecutionLogEntry key={entry.key} entry={entry} />
            ))}
          </div>
        )}

        <DialogFooter className="gap-2">
          {step < totalSteps && (
            <>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              {step > 1 && <Button variant="outline" onClick={() => setStep(s => s - 1)}><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>}
              <Button onClick={() => setStep(s => s + 1)}>
                {step === 1 ? 'Continue' : 'Next'} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}
          {step === totalSteps && !isExecutionPhase && (
            <>
              <Button variant="outline" onClick={() => setStep(s => s - 1)}><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button onClick={handleExecute} className="bg-green-600 hover:bg-green-700" data-testid="wizard-execute-btn">
                Confirm & Execute
              </Button>
            </>
          )}
          {step === totalSteps && isExecutionPhase && executionDone && (
            <Button onClick={handleClose} data-testid="wizard-done-btn">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Close Warning Dialog */}
      <Dialog open={showCloseWarning} onOpenChange={setShowCloseWarning}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Wizard Running
            </DialogTitle>
            <DialogDescription>
              The reactivation process is still running. Closing now will abort remaining steps. Are you sure?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCloseWarning(false)}>Continue Wizard</Button>
            <Button variant="destructive" onClick={() => { setShowCloseWarning(false); handleClose(); }}>Close & Abort</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};
